import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useIntl } from 'react-intl';
import { useQuickCreateExercise, QuickCreatedExercise } from 'hooks/coach/useQuickCreateExercise';

interface Props {
  visible: boolean;
  onHide: () => void;
  initialName?: string;
  onCreated?: (exercise: QuickCreatedExercise) => void;
}

export default function QuickCreateExerciseDialog({ visible, onHide, initialName = '', onCreated }: Props) {
  const intl = useIntl();
  const { loading, createExercise } = useQuickCreateExercise();
  const [name, setName] = useState<string>(initialName);

  useEffect(() => {
    if (visible) setName(initialName);
  }, [visible, initialName]);

  const handleHide = () => {
    if (loading) return;
    onHide();
  };

  const handleCreate = async () => {
    const created = await createExercise(name);
    if (created) {
      onCreated?.(created);
      onHide();
    }
  };

  return (
    <Dialog
      header={intl.formatMessage({ id: 'coach.quickCreateExercise.title' })}
      visible={visible}
      onHide={handleHide}
      draggable={false}
      resizable={false}
      dismissableMask={!loading}
      closable={!loading}
      style={{ width: '28rem', maxWidth: '94vw' }}
    >
      <div className="flex flex-column gap-3">
        <p className="m-0" style={{ color: 'var(--ios-text-secondary)', fontSize: '0.85rem' }}>
          {intl.formatMessage({ id: 'coach.quickCreateExercise.intro' })}
        </p>
        <div>
          <label
            htmlFor="quick-create-exercise-name"
            style={{
              display: 'block',
              fontSize: '0.72rem',
              fontWeight: 600,
              marginBottom: '0.3rem',
              color: 'var(--ios-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.03em'
            }}
          >
            {intl.formatMessage({ id: 'coach.quickCreateExercise.name' })}
          </label>
          <InputText
            id="quick-create-exercise-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim().length > 0 && !loading) {
                e.preventDefault();
                handleCreate();
              }
            }}
            autoFocus
            maxLength={255}
            disabled={loading}
            className="w-full"
            placeholder={intl.formatMessage({ id: 'coach.quickCreateExercise.placeholder' })}
            style={{
              borderRadius: 'var(--ios-radius-md)',
              border: '1px solid var(--ios-divider)'
            }}
          />
        </div>
        <div className="flex justify-content-end gap-2">
          <Button
            label={intl.formatMessage({ id: 'common.cancel' })}
            className="p-button-text"
            onClick={handleHide}
            disabled={loading}
          />
          <Button
            label={intl.formatMessage({ id: 'coach.quickCreateExercise.create' })}
            icon="pi pi-plus"
            onClick={handleCreate}
            loading={loading}
            disabled={name.trim().length === 0 || loading}
          />
        </div>
      </div>
    </Dialog>
  );
}
